import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Platform, Events } from '@ionic/angular';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, BehaviorSubject, from } from 'rxjs';
import { map, switchMap, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LocaleService } from './locale.service';

const helper = new JwtHelperService();
const TOKEN_KEY = 'jwt-token';
const API_KEY = environment.apiKey;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public user: Observable<any>;
  private userData = new BehaviorSubject(null);
  private authToken = new BehaviorSubject(null);
  public locale: string = null;


  private loginUrl: string = null;
  private registerUrl: string = null;
  private recoveryUrl: string = null;
  private changePasswordUrl: string = null;
  private appRecoverUrl: string = null;
  private allowInsecurePasswords = environment.allowInsecurePasswords;
  private redirectUrl = '/';

  constructor(
    private storage: Storage,
    private http: HttpClient,
    private plt: Platform,
    private events: Events,
    private router: Router,
    private localeService: LocaleService
  ) {
      this.loadStoredToken();
  }

  loadStoredToken() {
    const platformObs = from(this.plt.ready());

    this.user = platformObs.pipe(
      switchMap(() => from(this.storage.get(TOKEN_KEY))),
      map(token => {
        if (token) {
          const decoded = helper.decodeToken(token);
          if (helper.isTokenExpired(token)) {
            return null;
          }
          this.authToken.next(token);
          this.userData.next(decoded);
          this.events.publish('userloggedin', decoded);
          return true;
        } else {
          return null;
        }
      }),
      shareReplay(1)
    );
  }

  getAuthToken() {
    return this.authToken.getValue();
  }

  login(credentials: {username: string, pw: string}): Observable<any> {
    this.loginUrl = `${environment.apiUrl}/login_check?l=${this.localeService.getLocale()}`;

    return this.http.post<any>(this.loginUrl, {_username: credentials.username, _password: credentials.pw, _api_key: API_KEY}).pipe(
      map(response => {
        return response.token;
      }),
      switchMap(token => {
        const decoded = helper.decodeToken(token);
        this.authToken.next(token);
        this.userData.next(decoded);

        return from(this.storage.set(TOKEN_KEY, token)).pipe(
          map(() => {
            this.loadStoredToken();
            this.events.publish('loginsuccesfull', decoded);
            return decoded;
          })
        );
      })
    );
  }

  autologin(registerToken: string, internalClientId: string|null = null): Observable<any> {
    this.loginUrl = `${environment.apiUrl}/token-login?l=${this.localeService.getLocale()}`;

    let requestBody = {_register_token: registerToken, _api_key: API_KEY};
    if (internalClientId) {
      requestBody['_internal_client_id'] = internalClientId;
    }

    return this.http.post<any>(this.loginUrl, requestBody).pipe(
      map(response => {
        return response.token;
      }),
      switchMap(token => {
        const decoded = helper.decodeToken(token);
        this.authToken.next(token);
        this.userData.next(decoded);

        return from(this.storage.set(TOKEN_KEY, token)).pipe(
          map(() => {
            this.loadStoredToken();
            this.events.publish('autologinsuccesfull', decoded);
            return decoded;
          })
        );
      })
    );
  }

  verifyToken(token: string, internalClientId: string|null = null): Observable<any> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/register-token/verify?l=${this.localeService.getLocale()}`;
    let headers = new HttpHeaders().set('api-key', environment.apiKey);
    let requestBody = {_register_token: token};
    if (internalClientId) {
      requestBody['_internal_client_id'] = internalClientId;
    }

    return this.http.post<any>(url, requestBody, { headers }).pipe(
      map(response => {
        return response.data.register;
      })
    );
  }

  register(requestBody: object, autologinAfter = false, internalClientId: string|null = null): Observable<any> {
    this.registerUrl = `${environment.apiUrl}/register?l=${this.localeService.getLocale()}`;

    if (internalClientId) {
      requestBody['_internal_client_id'] = internalClientId;
    }

    return this.http.post<any>(this.registerUrl, requestBody).pipe(
      map(response => {
        return response;
      }),
      switchMap(() => {
        if (!autologinAfter) {
          return this.login({username: requestBody['_username'], pw: requestBody['_password']})
        }
        if (autologinAfter) {
          return this.autologin(requestBody['_register_token'], internalClientId);
        }
      })
    );
  }

  recovery(username: string): Observable<any> {
    this.recoveryUrl = `${environment.apiUrl}/recovery?l=${this.localeService.getLocale()}`;
    this.appRecoverUrl = `${environment.appUrl}/${this.localeService.getLocale()}/change-password`;
    return this.http.post<any>(this.recoveryUrl, {
      _url: this.appRecoverUrl,
      _username: username,
      _api_key: API_KEY,
      _mail_transport: environment.mailTransport,
      _mail_brand_name: environment.brandName
    }).pipe(
      map(response => {
        return response.message ? response.message : '';
      })
    );
  }

  changePassword(password: string, token: string): Observable<any> {
    this.changePasswordUrl = `${environment.apiUrl}/change-password?l=${this.localeService.getLocale()}`;
    return this.http.post<any>(this.changePasswordUrl, {
      _password: password,
      _token: token,
      _api_key: API_KEY
    }).pipe(
      map(response => {
        return response.message ? response.message : '';
      })
    );
  }

  getUser() {
    return this.userData.getValue();
  }

  logout() {
    this.setRedirectUrl('/service-index');
    localStorage.removeItem('flyingSocioSanitaryProfile');
    this.storage.remove(TOKEN_KEY).then(() => {
      this.router.navigateByUrl('/');
      this.userData.next(null);
    });
  }

  isValidPassword(plainPassword: string): boolean {
    if (this.allowInsecurePasswords || !plainPassword) {
        return true;
    }

    // Expresión regular para validar la contraseña
    const passwordRegex = /^(?=.*[a-zA-ZÀ-ÖØ-öø-ÿ])(?=.*[A-ZÀ-ÖØ-Þ])(?=.*[a-zà-öø-ÿ])(?=.*\d)(?=.*[^a-zA-ZÀ-ÖØ-öø-ÿ\d]).{8,}$/;

    return passwordRegex.test(plainPassword);
  }

  setRedirectUrl(url: string) {
    // const updatedString = url.replace('/es', '');
    // this.redirectUrl = url.replace('/es', '');
    this.redirectUrl = url;
  }

  getRedirectUrl(): string {
    return this.redirectUrl;
  }
}
